import { IconButton, Tooltip } from '@mui/material';
import { getGridStringOperators, GridColDef } from '@mui/x-data-grid-pro';
import moment from 'moment/moment';

import { IcPopUp } from '@/assets/images';
import { excludeValues } from '@/components/DataGrid/utils';
import { DATE_FORMAT } from '@/config';
import { KaseStatusChip } from '@/layouts/TabLayout/MasterNames/styles';
import { IAccount, IUser } from '@/models';
import { getAddressLocation, validateRoute } from '@/services';
import { useToastStore, useUserStore } from '@/store';

interface RelatedRecordRowData {
  _id?: string;
  type?: string;
}

const relatedRecordAccessDeniedMessage =
  'You do not have permission to open this related record.';

const normalizeRecordType = (type?: string) =>
  type
    ?.trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

export const getRelatedRecordPath = (row?: RelatedRecordRowData) => {
  const recordId = row?._id;
  const recordType = normalizeRecordType(row?.type);

  if (!recordId || !recordType) return '';

  switch (recordType) {
    case 'CFS':
      return `/cfs/${recordId}?newTab=true`;
    case 'KASE':
    case 'CASE':
      return `/cases/${recordId}/overview`;
    case 'TASK':
    case 'TASKS':
      return `/tasks/${recordId}`;
    case 'FIRE_CASE':
    case 'FIRE_KASE':
      return `/fire-cases/${recordId}`;
    default:
      return '';
  }
};

export const canOpenRelatedRecord = (
  row?: RelatedRecordRowData,
  user?: IUser | null,
  account?: IAccount | null,
) => {
  const path = getRelatedRecordPath(row);
  if (!path) return false;

  return validateRoute(path, user ?? undefined, account ?? undefined);
};

export const openRelatedRecordInNewTab = (row?: RelatedRecordRowData) => {
  const path = getRelatedRecordPath(row);

  if (!path) return;

  window.open(path, '_blank', 'noopener');
};

const RelatedRecordOpenAction = ({ row }: { row?: RelatedRecordRowData }) => {
  const { user, account } = useUserStore();
  const { updateToast } = useToastStore();
  const path = getRelatedRecordPath(row);
  const canOpen = canOpenRelatedRecord(row, user, account);
  const isDisabled = !path || !canOpen;
  const showNoAccessTooltip = Boolean(path) && !canOpen;

  return (
    <Tooltip
      title={showNoAccessTooltip ? relatedRecordAccessDeniedMessage : ''}
      disableHoverListener={!showNoAccessTooltip}
      disableFocusListener={!showNoAccessTooltip}
      disableTouchListener={!showNoAccessTooltip}
    >
      <span>
        <IconButton
          disabled={isDisabled}
          onClick={(event) => {
            event.stopPropagation();
            if (!canOpen) {
              updateToast({
                open: true,
                message: relatedRecordAccessDeniedMessage,
              });
              return;
            }
            openRelatedRecordInNewTab(row);
          }}
        >
          <IcPopUp className="text-gray-400" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const number = (): Partial<GridColDef> => ({
  field: 'name',
  renderCell: (params) => {
    return params.row.name;
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),

  sortComparator: (a, b) =>
    (a?.toLowerCase() || '').localeCompare(b?.toLowerCase() || ''),
});

const location = (): Partial<GridColDef> => ({
  field: 'fullAddressSortable',
  renderCell: (params) => {
    return getAddressLocation(params.row.address) || 'No Address';
  },
});

const dateAndTime = (): Partial<GridColDef> => ({
  field: 'createdAt',
  renderCell: (params) =>
    moment(params.row.createdAt).format(DATE_FORMAT) || '/',
});
const involvement = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    if (!params.row.relationship?.involvement) {
      return '/';
    }
    const involvementValue = params.row.relationship.involvement;
    const involvementArray = Array.isArray(involvementValue)
      ? involvementValue
      : [involvementValue];
    return involvementArray.map((item: string, index: number) => (
      <KaseStatusChip key={`${item}-${index}`} label={item} />
    ));
  },
  sortable: false,
});

const actions = (): Partial<GridColDef> => ({
  sortable: false,
  filterable: false,
  renderCell: (params) => <RelatedRecordOpenAction row={params.row} />,
});

export const relatedRecords = (_navigate?: (path: string) => void) => {
  void _navigate;

  return {
    number,
    location,
    dateAndTime,
    involvement,
    actions,
  };
};
